'use client';

import { datadogLogs, type Logger } from '@datadog/browser-logs';
import { DataDogBrowserLogsTransport } from '@loglayer/transport-datadog-browser-logs';
import { ConsoleTransport, LogLayer } from 'loglayer';
import { serializeError } from 'serialize-error';

import { getLegacyEnvVariable } from '@/app/components/LegacyEnvVariables/util';

import { LOGGER_NAME } from './consts';
import type { LogPayload } from './types';

let logger: Logger | undefined;

const initLogger = (): Logger => {
  const clientToken = getLegacyEnvVariable('NEXT_PUBLIC_DD_CLIENT_TOKEN');
  const ouraEnv = getLegacyEnvVariable('NEXT_PUBLIC_OURA_ENV');

  if (!clientToken) {
    throw new Error(
      'No Datadog client token set, please add the NEXT_PUBLIC_DD_CLIENT_TOKEN environment variable.',
    );
  }

  datadogLogs.init({
    clientToken,
    env: ouraEnv,
    forwardErrorsToLogs: true,
    service: LOGGER_NAME,
    sessionSampleRate: 100,
  });

  datadogLogs.addLoggerGlobalContext('env', ouraEnv);

  return datadogLogs.createLogger(LOGGER_NAME);
};

const logToDatadogClient = ({
  data = {},
  error,
  level = 'info',
  message,
  module,
}: LogPayload) => {
  const isDevelopment =
    getLegacyEnvVariable('NEXT_PUBLIC_OURA_ENV') === 'development';

  let log: LogLayer;

  // Don't send logs to Datadog for local development
  if (!isDevelopment) {
    if (!logger) {
      logger = initLogger();
    }

    log = new LogLayer({
      errorSerializer: serializeError,
      transport: new DataDogBrowserLogsTransport({
        logger,
      }),
    });
  } else {
    log = new LogLayer({
      errorSerializer: serializeError,
      transport: new ConsoleTransport({
        level,
        logger: console,
      }),
    });
  }

  return log
    .withError(error)
    .withMetadata({ ...data, module })
    [level](message);
};

export default logToDatadogClient;
